import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../auth/authContext.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { useOpportunityForecast } from '../opportunities/api.js';
import { OpportunityForecastPanel } from '../opportunities/OpportunityForecastPanel.js';
import { AgreementForecastPanel, filterAgreementTotals } from './AgreementForecastPanel.js';
import { ForecastPortalCard } from './ForecastPortalCard.js';
import { ForecastSummaryGrids, type ForecastSummaryArea } from './ForecastSummaryGrids.js';
import { ProjectForecastPanel, filterProjectTotals } from './ProjectForecastPanel.js';
import { TicketForecastPanel } from './TicketForecastPanel.js';
import { useAgreementForecast, useProjectForecast, useTicketForecast } from './api.js';

/**
 * Master forecast (Insights): two combined totals grids (by year then by month) at the
 * top, then the opportunity, agreement, project, and ticket revenue forecasts; the public
 * link card sits at the bottom. Each panel is gated by its own permission so a user only
 * sees the forecasts they can read; the combined totals sum only the forecasts they can
 * see. The agreement active/draft toggles are lifted here so the combined totals react to
 * them.
 */
export function ForecastPage(): JSX.Element {
  const { can, isAdmin } = useAuth();
  const navigate = useNavigate();

  const canSales = can('sales');
  const canAgreements = can('agreements');
  const canProjects = can('projects');
  const canTickets = can('tickets');

  const opportunityQuery = useOpportunityForecast({}, canSales);
  const agreementQuery = useAgreementForecast(canAgreements);
  const projectQuery = useProjectForecast(canProjects);
  const ticketQuery = useTicketForecast(canTickets);

  // Lifted so the combined totals grids recompute when the agreement toggles change.
  const [showActiveAgreements, setShowActiveAgreements] = useState(true);
  const [showDraftAgreements, setShowDraftAgreements] = useState(true);
  // Lifted so the combined totals grids recompute when the project series toggles change.
  const [showRemainingProjects, setShowRemainingProjects] = useState(true);
  const [showUnbilledProjects, setShowUnbilledProjects] = useState(false);

  const areas: ForecastSummaryArea[] = [];
  if (opportunityQuery.data) {
    areas.push({
      key: 'opportunities',
      label: 'Opportunities',
      totalsByMonth: opportunityQuery.data.totalsByMonth,
      total: opportunityQuery.data.total,
    });
  }
  if (agreementQuery.data) {
    const filtered = filterAgreementTotals(
      agreementQuery.data,
      showActiveAgreements,
      showDraftAgreements,
    );
    areas.push({
      key: 'agreements',
      label: 'Agreements',
      totalsByMonth: filtered.totalsByMonth,
      total: filtered.total,
    });
  }
  if (projectQuery.data) {
    const filtered = filterProjectTotals(
      projectQuery.data,
      showRemainingProjects,
      showUnbilledProjects,
    );
    areas.push({
      key: 'projects',
      label: 'Projects',
      totalsByMonth: filtered.totalsByMonth,
      total: filtered.total,
    });
  }
  if (ticketQuery.data) {
    areas.push({
      key: 'tickets',
      label: 'Tickets',
      totalsByMonth: ticketQuery.data.totalsByMonth,
      total: ticketQuery.data.total,
    });
  }

  return (
    <div>
      <PageHeader
        title="Forecast"
        description="Combined future-revenue forecast across opportunities, agreements, projects, and tickets."
      />

      <ForecastSummaryGrids areas={areas} />

      {canSales ? (
        <OpportunityForecastPanel
          data={opportunityQuery.data}
          onOpenOpportunity={() => navigate('/sales/opportunities')}
        />
      ) : null}
      {canAgreements ? (
        <AgreementForecastPanel
          data={agreementQuery.data}
          defaultCollapsed
          showActive={showActiveAgreements}
          onShowActiveChange={setShowActiveAgreements}
          showDraft={showDraftAgreements}
          onShowDraftChange={setShowDraftAgreements}
        />
      ) : null}
      {canProjects ? (
        <ProjectForecastPanel
          data={projectQuery.data}
          defaultCollapsed
          showRemaining={showRemainingProjects}
          onShowRemainingChange={setShowRemainingProjects}
          showUnbilled={showUnbilledProjects}
          onShowUnbilledChange={setShowUnbilledProjects}
        />
      ) : null}
      {canTickets ? <TicketForecastPanel data={ticketQuery.data} defaultCollapsed /> : null}

      {!canSales && !canAgreements && !canProjects && !canTickets ? (
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-6 text-sm text-slate-500 shadow-sm">
          You don’t have access to any forecast data.
        </div>
      ) : null}

      {isAdmin ? <ForecastPortalCard /> : null}
    </div>
  );
}
